/**
 * The face detector behind the selfie ring, in two tiers.
 *
 *   1. The browser's native `FaceDetector` when it exists — instant, zero download. Chrome
 *      keeps it behind a flag, so in practice this almost never fires.
 *   2. MediaPipe's BlazeFace, self-hosted. ~3.3MB gzipped of WASM plus a 224KB model,
 *      fetched ONCE and then cached by the browser, and only ever on a step that opens the
 *      camera. That cost buys the one thing tier 1 cannot deliver: detection that works on
 *      iOS Safari, which is where a large share of applicants are.
 *
 * Everything is self-hosted under /mediapipe and /models rather than loaded from Google's
 * CDN. Two reasons, neither of them theoretical: a KYC flow should not hand a third party a
 * request every time somebody opens their camera, and an applicant mid-verification must not
 * be stopped by an outage on a host we do not control. Self-hosting also survives a CSP being
 * added later, which a KYC product will want eventually.
 *
 * IMPORTANT — it degrades, it never blocks. If the WASM or the model fails to load (offline,
 * a proxy, an unsupported device), detection reports "unknown" and the caller falls back to
 * the exposure-only check. An applicant must never be trapped behind a red ring because an
 * asset 404'd.
 */

/** A face's box in video pixel coordinates. */
export interface FaceBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * `undefined` = no detector could run (caller falls back to exposure only).
 * An array = the detector ran; empty means it genuinely saw no face.
 */
export type FaceDetectionResult = FaceBox[] | undefined;

// ── Tier 1: the native Shape Detection API ────────────────────────────────────────────

interface NativeDetectedFace {
  boundingBox: { x: number; y: number; width: number; height: number };
}

interface NativeFaceDetector {
  detect(source: ImageBitmapSource): Promise<NativeDetectedFace[]>;
}

interface NativeFaceDetectorCtor {
  new (options?: { maxDetectedFaces?: number; fastMode?: boolean }): NativeFaceDetector;
}

function nativeCtor(): NativeFaceDetectorCtor | undefined {
  if (typeof window === 'undefined') return undefined;
  return (window as unknown as { FaceDetector?: NativeFaceDetectorCtor }).FaceDetector;
}

let nativeDetector: NativeFaceDetector | null = null;

// ── Tier 2: MediaPipe BlazeFace ───────────────────────────────────────────────────────

/**
 * Loaded lazily and ONCE, on the first frame of the first camera step. Importing
 * `@mediapipe/tasks-vision` at module scope would pull its bundle into the main chunk and
 * cost every applicant the download, including the ones who never reach a face step.
 */
let mediapipePromise: Promise<MediapipeDetector | null> | null = null;

interface MediapipeDetector {
  detectForVideo(
    video: HTMLVideoElement,
    timestampMs: number,
  ): {
    detections: {
      boundingBox?: { originX: number; originY: number; width: number; height: number };
    }[];
  };
}

async function loadMediapipe(): Promise<MediapipeDetector | null> {
  try {
    const { FaceDetector, FilesetResolver } = await import('@mediapipe/tasks-vision');
    const fileset = await FilesetResolver.forVisionTasks('/mediapipe/wasm');
    const detector = await FaceDetector.createFromOptions(fileset, {
      baseOptions: {
        modelAssetPath: '/models/blaze_face_short_range.tflite',
        // GPU where available, which is most phones; the runtime falls back to CPU itself.
        delegate: 'GPU',
      },
      runningMode: 'VIDEO',
      // A selfie at arm's length is a large, high-confidence face. A generous floor here
      // would flicker the ring on hands, patterns and background objects.
      minDetectionConfidence: 0.6,
    });
    return detector as unknown as MediapipeDetector;
  } catch {
    // Asset 404, blocked request, unsupported device — the caller degrades to the exposure
    // check rather than blocking the applicant.
    return null;
  }
}

/**
 * Whether a detector is available WITHOUT a download — i.e. the native API. False does not
 * mean detection is impossible, only that the MediaPipe path has to load first.
 */
export function hasInstantFaceDetector(): boolean {
  return nativeCtor() !== undefined;
}

/**
 * Faces in the current frame, or `undefined` when no detector could run.
 *
 * `timestampMs` must increase between calls — MediaPipe's VIDEO running mode rejects a
 * timestamp that goes backwards, which is why the caller passes a monotonic clock rather
 * than letting this read one itself.
 */
export async function detectFaces(
  video: HTMLVideoElement,
  timestampMs: number,
): Promise<FaceDetectionResult> {
  const Ctor = nativeCtor();
  if (Ctor) {
    try {
      nativeDetector ??= new Ctor({ maxDetectedFaces: 2, fastMode: true });
      const faces = await nativeDetector.detect(video);
      return faces.map((f) => ({
        x: f.boundingBox.x,
        y: f.boundingBox.y,
        width: f.boundingBox.width,
        height: f.boundingBox.height,
      }));
    } catch {
      // Fall through to MediaPipe rather than reporting "no face" — a detector that threw
      // has told us nothing about the frame.
    }
  }

  mediapipePromise ??= loadMediapipe();
  const detector = await mediapipePromise;
  if (!detector) return undefined;

  try {
    const result = detector.detectForVideo(video, timestampMs);
    return result.detections
      .map((d) => d.boundingBox)
      .filter((b): b is NonNullable<typeof b> => b !== undefined)
      .map((b) => ({ x: b.originX, y: b.originY, width: b.width, height: b.height }));
  } catch {
    return undefined;
  }
}
